import { useEffect, useState } from 'react';
import { toast } from '@/components/ui/toaster.jsx';
import { correctPhfField, getAnswerLists } from '@/services/pilots.service.js';

// ONE CLICK ON THE ANSWER ITSELF (David 2026-11-04: "Correcting a pilot's answer is one click on the answer itself, wherever
// it's shown (the record, the roster, the pre-submit dialog): edit, save, done. A reason is optional, never required."). The
// value is the control: click it, pick or type, and it saves (a pick saves at once; a typed value on Enter or Save). A fixed
// answer is a pick from its one list (GET /api/tracon/answer-lists, utils/answer-lists), never free text; a stored value that
// is not on the list is shown as it is, marked, and is never guessed onto the list. It posts through the one correction door
// (PATCH /api/tracon/phf/:id/field), which keeps the pilot's own attestation and records who and when.
//   field    the PHF column (utils/phf-correctable CORRECTABLE)
//   phfId    the form the correction writes (the one the surface shows); target: that form in words, said on hover
//   current  the stored value; children: how the surface shows it
//   onSaved  called with the server's answer after a save
export const CHOICE_FIELDS = new Set(['certificate_type', 'medical_class']);
export function kindOf(field) {
  if (CHOICE_FIELDS.has(field)) return 'choice';
  if (/_date$|date_of_birth/.test(field)) return 'date';
  if (/hours/.test(field)) return 'number';
  return 'text';
}

export default function AnswerEdit({ field, phfId, current, label, target = null, onSaved, children, className = '' }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState(null);
  const kind = kindOf(field);

  useEffect(() => {
    if (!open || kind !== 'choice' || options) return;
    getAnswerLists().then((l) => setOptions(l[field] || [])).catch(() => toast.error('The list for this answer could not be read; nothing was changed'));
  }, [open, kind, field, options]);

  if (!phfId) return <>{children}</>;

  const start = () => { setValue(current == null ? '' : String(current).slice(0, kind === 'date' ? 10 : undefined)); setNote(''); setOpen(true); };
  const save = async (v) => {
    const next = String(v == null ? '' : v).trim();
    if (saving) return;
    if (kind === 'number' && next !== '' && !Number.isFinite(Number(next.replace(/,/g, '')))) { toast.error(`"${next}" is not a number of hours`); return; }
    setSaving(true);
    try {
      const r = await correctPhfField(phfId, { field, value: next, reason: note.trim() || null });
      toast.success(r?.unchanged ? `${label}: already that value` : `${label} saved`);
      setOpen(false);
      onSaved?.(r);
    } catch (err) {
      toast.error(err?.body?.error || err?.message || `${label} could not be saved`);
    } finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={start} title={`Click to edit ${label.toLowerCase()}${target ? ` (writes ${target})` : ''}`} data-answer-edit={field} data-answer-phf={phfId}
        className={`inline min-h-[28px] cursor-text rounded-sm border-b border-dotted border-border text-left hover:bg-accent-soft/40 ${className}`}>
        {children ?? <span className="text-text-dim">not answered</span>}
      </button>
    );
  }

  const offList = kind === 'choice' && options && current != null && String(current).trim() !== '' && !options.some((o) => o.value === current);
  return (
    <span className="inline-flex flex-wrap items-center gap-2" data-answer-editing={field}>
      {kind === 'choice' ? (
        <select autoFocus disabled={saving || !options} value={offList ? '__off__' : value} aria-label={label}
          onChange={(e) => { if (e.target.value && e.target.value !== '__off__') save(e.target.value); }}
          onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
          className="min-h-[36px] rounded border border-accent bg-bg px-2 text-sm text-text">
          {offList ? <option value="__off__" disabled>{`${current} (not on the list: pick the right one)`}</option> : null}
          {!value && !offList ? <option value="">Choose</option> : null}
          {(options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input autoFocus type={kind === 'date' ? 'date' : 'text'} inputMode={kind === 'number' ? 'decimal' : undefined} value={value} aria-label={label} disabled={saving}
          onChange={(e) => setValue(e.target.value)} onFocus={(e) => kind !== 'date' && e.target.select()}
          onKeyDown={(e) => { if (e.key === 'Enter') save(value); if (e.key === 'Escape') setOpen(false); }}
          className="min-h-[36px] w-36 rounded border border-accent bg-bg px-2 font-mono text-sm text-text" />
      )}
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why it changed" aria-label={`Why ${label.toLowerCase()} changed`} disabled={saving}
        onKeyDown={(e) => { if (e.key === 'Enter' && kind !== 'choice') save(value); if (e.key === 'Escape') setOpen(false); }}
        className="min-h-[36px] w-44 rounded border border-border bg-bg px-2 text-xs text-text placeholder:text-text-dim" />
      {kind !== 'choice' ? <button type="button" disabled={saving} onClick={() => save(value)} className="min-h-[36px] rounded-card bg-accent px-3 text-sm font-medium text-white disabled:opacity-60">{saving ? 'Saving' : 'Save'}</button> : null}
      <button type="button" disabled={saving} onClick={() => setOpen(false)} className="min-h-[36px] px-2 text-sm text-text-dim hover:text-text">Cancel</button>
    </span>
  );
}
